/**
 * URL a player should load for a song. Prefers the compressed playback copy
 * the backend writes (`streamUrl`, ~3-6 MB AAC) over the original upload,
 * which is usually a 20-70 MB WAV.
 */
export function getPlayableUrl(song) {
  // Not released yet: the API sends no audio (docs/AUDIO_ACCESS.md).
  if (!song || song.audioLocked) return null;
  return song.streamUrl || song.fileId?.filePath || null;
}

// Signed audio URLs (docs/AUDIO_ACCESS.md) expire, typically after an hour.
// A link counts as stale this long before it expires, so a song is never
// started on a link that dies a minute in.
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;

/**
 * When a signed audio URL stops working, in ms since the epoch, or null for
 * an unsigned URL. Reads S3 presigned (X-Amz-Date + X-Amz-Expires) and
 * CloudFront canned-policy (Expires) links.
 */
export function signedUrlExpiresAt(url) {
  if (typeof url !== "string") return null;
  let params;
  try {
    params = new URL(url).searchParams;
  } catch (_) {
    return null;
  }
  const amzDate = params.get("X-Amz-Date");
  const amzExpires = Number(params.get("X-Amz-Expires"));
  if (amzDate && amzExpires) {
    const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate);
    if (!match) return null;
    const [, y, mo, d, h, mi, s] = match.map(Number);
    return Date.UTC(y, mo - 1, d, h, mi, s) + amzExpires * 1000;
  }
  const cloudFrontExpires = Number(params.get("Expires"));
  if (cloudFrontExpires && (params.get("Signature") || params.get("Key-Pair-Id"))) return cloudFrontExpires * 1000;
  return null;
}

/** Whether a URL can still be started: unsigned, or signed and not about to expire. */
export function isPlayableUrlFresh(url, now = Date.now()) {
  const expiresAt = signedUrlExpiresAt(url);
  return expiresAt === null || expiresAt - EXPIRY_MARGIN_MS > now;
}
